/**
 * Command line chrome hook (Faz 11.9 — OCS command_line.rs parity).
 *
 * Shared by the 2D inline command line (Mode2D) and the 3D CommandLine
 * panel so both speak the same three sysvars:
 *  - CLIPROMPTLINES: the last N history lines stay pinned, visible above
 *    the input row (the older lines keep scrolling).
 *  - COMMANDLINEFADETIME: once the transcript stops growing (a command
 *    finished) the history fades to a whisper; `wake()` (typing) restores
 *    it instantly.
 *  - COMMANDLINEOPACITY: panel background opacity percent (20-100).
 */

import { useEffect, useMemo, useState } from 'react';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';

export function useCommandLineChrome() {
    const promptLines = useSystemVarsStore((s) => s.CLIPROMPTLINES);
    const fadeMs = useSystemVarsStore((s) => s.COMMANDLINEFADETIME);
    const opacity = useSystemVarsStore((s) => s.COMMANDLINEOPACITY);
    const commandLog = useDynamicInputStore((s) => s.commandLog);
    const [faded, setFaded] = useState(false);

    // The transcript only grows while commands emit output; when it stops
    // (command finished) the fade timer runs. FADETIME 0 = never fade.
    useEffect(() => {
        setFaded(false);
        if (fadeMs <= 0) return;
        const timer = window.setTimeout(() => setFaded(true), fadeMs);
        return () => window.clearTimeout(timer);
    }, [commandLog.length, fadeMs]);

    /** Typing restores the history instantly (OCS wake semantics). */
    const wake = () => setFaded(false);

    const pinnedLines = useMemo(
        () => (promptLines > 0 ? commandLog.slice(-promptLines) : []),
        [commandLog, promptLines]
    );
    const scrolledLines = useMemo(
        () => (promptLines > 0 ? commandLog.slice(0, -promptLines) : commandLog),
        [commandLog, promptLines]
    );

    // Dynamic panel translucency — a computed runtime value, not static style.
    const panelStyle = {
        backgroundColor: `color-mix(in srgb, var(--panel-background) ${opacity}%, transparent)`,
    } as const;

    const historyStyle = {
        opacity: faded ? 0.25 : 1,
        transition: `opacity 400ms ease`,
    } as const;

    return { promptLines, pinnedLines, scrolledLines, faded, wake, panelStyle, historyStyle, fadeMs };
}
